import { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  QUICK_PERIODS, getQuickRange, BILLABLE_STATUSES,
  getDisplayStatus, tripExGST, exportCSV,
} from '../../utils/reportHelpers';

const SUPPORT_ROLES = ['support_worker', 'carer'];

function isSupportRole(role) {
  return SUPPORT_ROLES.includes(role);
}

function monthKey(dateStr) {
  // dateStr is "YYYY-MM-DD"; return "Mon YYYY"
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
}

function roleLabel() {
  return 'Support Worker';
}

export default function SupportWorkerReport({ trips }) {
  const now = new Date();
  const fy  = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;

  const [periodKey, setPeriodKey] = useState('thisFY');
  const [from,      setFrom]      = useState(`${fy}-07-01`);
  const [to,        setTo]        = useState(`${fy + 1}-06-30`);
  const [ccFilter,  setCcFilter]  = useState('');

  const applyPreset = (key) => {
    setPeriodKey(key);
    if (key === 'custom') return;
    const r = getQuickRange(key);
    setFrom(r.from);
    setTo(r.to);
  };

  // All cost centres present in the data set (for filter dropdown)
  const costCentres = useMemo(() => {
    const s = new Set(trips.map(t => t.costCentre).filter(Boolean));
    return [...s].sort();
  }, [trips]);

  // All trips in period that pass BILLABLE_STATUSES
  const billableInPeriod = useMemo(() => {
    return trips.filter(t => {
      const ds = getDisplayStatus(t);
      if (!BILLABLE_STATUSES.has(ds)) return false;
      if (from && (t.startDate || '') < from) return false;
      if (to   && (t.startDate || '') > to)   return false;
      if (ccFilter && t.costCentre !== ccFilter) return false;
      return true;
    });
  }, [trips, from, to, ccFilter]);

  // Trips that include at least one support worker or carer passenger
  const supportTrips = useMemo(() => {
    return billableInPeriod.filter(t => {
      const ap = t.additionalPassengers || [];
      return ap.some(p => isSupportRole(p.role));
    });
  }, [billableInPeriod]);

  // Total billable spend (all trips in period, for % calculation)
  const totalBillableSpend = useMemo(() => {
    return billableInPeriod.reduce((s, t) => s + tripExGST(t), 0);
  }, [billableInPeriod]);

  // Per-trip proportional cost and support worker list
  const enrichedTrips = useMemo(() => {
    return supportTrips.map(trip => {
      const ap = trip.additionalPassengers || [];
      const supportWorkers  = ap.filter(p => isSupportRole(p.role));
      const totalPassengers = 1 + ap.length; // 1 = primary traveller
      const supportCount    = supportWorkers.length;
      const tripCostEx      = tripExGST(trip);
      const supportCost     = tripCostEx * (supportCount / totalPassengers);
      return { trip, supportWorkers, supportCount, totalPassengers, tripCostEx, supportCost };
    });
  }, [supportTrips]);

  // Summary stats
  const summary = useMemo(() => {
    const totalSupportCost = enrichedTrips.reduce((s, r) => s + r.supportCost, 0);
    const pct = totalBillableSpend > 0 ? (totalSupportCost / totalBillableSpend) * 100 : 0;

    // Unique support worker names
    const nameSet = new Set();
    enrichedTrips.forEach(({ supportWorkers }) => {
      supportWorkers.forEach(p => { if (p.name) nameSet.add(p.name.trim()); });
    });

    return {
      tripCount:        enrichedTrips.length,
      totalSupportCost,
      pct,
      uniqueWorkers:    nameSet.size,
    };
  }, [enrichedTrips, totalBillableSpend]);

  // Bar chart: support cost by month
  const chartData = useMemo(() => {
    const map = {};
    enrichedTrips.forEach(({ trip, supportCost }) => {
      const mk = monthKey(trip.startDate);
      if (!map[mk]) map[mk] = { month: mk, cost: 0, sortKey: trip.startDate?.slice(0, 7) || '' };
      map[mk].cost += supportCost;
    });
    return Object.values(map).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [enrichedTrips]);

  // Detail rows: one row per support worker per trip, sorted by startDate desc
  const detailRows = useMemo(() => {
    const rows = [];
    enrichedTrips.forEach(({ trip, supportWorkers, supportCost, supportCount }) => {
      const costPerWorker = supportCount > 0 ? supportCost / supportCount : 0;
      supportWorkers.forEach(worker => {
        rows.push({
          travellerName: trip.travellerName || '—',
          workerName:    worker.name || '—',
          role:          worker.role,
          tripTitle:     trip.title || trip.tripRef || '—',
          destination:   trip.destinationCity || '—',
          startDate:     trip.startDate || '',
          estCost:       costPerWorker,
          costCentre:    trip.costCentre || '—',
          tripType:      trip.tripType || '—',
        });
      });
    });
    return rows.sort((a, b) => b.startDate.localeCompare(a.startDate));
  }, [enrichedTrips]);

  const handleExport = () => {
    const headers = [
      'Traveller', 'Support Staff', 'Role', 'Trip', 'Destination',
      'Date', 'Est. Cost (Ex-GST)', 'Cost Centre', 'Trip Type',
    ];
    const rows = detailRows.map(r => [
      r.travellerName,
      r.workerName,
      roleLabel(r.role),
      r.tripTitle,
      r.destination,
      r.startDate,
      r.estCost.toFixed(2),
      r.costCentre,
      r.tripType,
    ]);
    exportCSV([headers, ...rows], `support_worker_report_${from}_to_${to}.csv`);
  };

  const fmt = (n) =>
    `A$${n.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <div style={{ fontFamily: "'DM Sans','Helvetica Neue',sans-serif" }}>

      {/* Filters */}
      <div style={card}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
          <div>
            <label style={lbl}>Period</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {QUICK_PERIODS.map(p => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  style={{
                    ...pill,
                    background:   periodKey === p.key ? '#0d9488' : undefined,
                    color:        periodKey === p.key ? '#fff'    : undefined,
                    borderColor:  periodKey === p.key ? '#0d9488' : undefined,
                  }}
                >
                  {p.label}
                </button>
              ))}
              <button
                onClick={() => applyPreset('custom')}
                style={{
                  ...pill,
                  background:  periodKey === 'custom' ? '#0d9488' : undefined,
                  color:       periodKey === 'custom' ? '#fff'    : undefined,
                  borderColor: periodKey === 'custom' ? '#0d9488' : undefined,
                }}
              >
                Custom
              </button>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div>
                <label style={lbl}>From</label>
                <input
                  type="date" value={from}
                  onChange={e => { setFrom(e.target.value); setPeriodKey('custom'); }}
                  style={inp}
                />
              </div>
              <div>
                <label style={lbl}>To</label>
                <input
                  type="date" value={to}
                  onChange={e => { setTo(e.target.value); setPeriodKey('custom'); }}
                  style={inp}
                />
              </div>
            </div>
          </div>

          {costCentres.length > 0 && (
            <div>
              <label style={lbl}>Cost Centre</label>
              <select value={ccFilter} onChange={e => setCcFilter(e.target.value)} style={inp}>
                <option value="">All</option>
                {costCentres.map(cc => <option key={cc} value={cc}>{cc}</option>)}
              </select>
            </div>
          )}
        </div>

        <p style={{ fontSize: 11, color: '#78716c', fontStyle: 'italic', margin: 0 }}>
          Costs are proportionally estimated — not tracked per person
        </p>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12, marginBottom: 20 }}>
        <div style={summaryCard}>
          <div style={summaryLabel}>Total trips with support</div>
          <div style={summaryNumber}>{summary.tripCount}</div>
        </div>
        <div style={summaryCard}>
          <div style={summaryLabel}>Estimated support cost (ex-GST)</div>
          <div style={summaryNumber}>{fmt(summary.totalSupportCost)}</div>
        </div>
        <div style={summaryCard}>
          <div style={summaryLabel}>% of total billable travel spend</div>
          <div style={summaryNumber}>{summary.pct.toFixed(1)}%</div>
        </div>
        <div style={summaryCard}>
          <div style={summaryLabel}>Support workers / carers listed</div>
          <div style={summaryNumber}>{summary.uniqueWorkers}</div>
        </div>
      </div>

      {enrichedTrips.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#78716c' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>—</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#57534e' }}>
            No trips with support workers or carers in this period.
          </div>
        </div>
      ) : (
        <>
          {/* Bar chart */}
          {chartData.length > 0 && (
            <div style={{ ...card, marginBottom: 20 }}>
              <div style={sectionHeading}>Estimated Support Cost by Month</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: '#78716c' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#78716c' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                    width={48}
                  />
                  <Tooltip
                    formatter={(value) => [
                      `A$${value.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
                      'Est. support cost (ex-GST)',
                    ]}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                  />
                  <Bar dataKey="cost" fill="#d97706" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Detail table */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <div style={sectionHeading}>Trip Detail</div>
            <button onClick={handleExport} style={exportBtn}>
              Export CSV
            </button>
          </div>

          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: 16 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th scope="col" style={th}>Traveller</th>
                    <th scope="col" style={th}>Support staff</th>
                    <th scope="col" style={th}>Role</th>
                    <th scope="col" style={th}>Trip</th>
                    <th scope="col" style={th}>Destination</th>
                    <th scope="col" style={th}>Date</th>
                    <th scope="col" style={{ ...th, textAlign: 'right' }}>Est. cost (ex-GST)</th>
                  </tr>
                </thead>
                <tbody>
                  {detailRows.map((row, idx) => (
                    <tr
                      key={`${row.tripTitle}-${row.workerName}-${idx}`}
                      style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f1f5f9' }}
                    >
                      <td style={td}>{row.travellerName}</td>
                      <td style={{ ...td, fontWeight: 500, color: '#0f172a' }}>{row.workerName}</td>
                      <td style={td}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                          background: '#eff6ff',
                          color:      '#2563eb',
                          border:     '1px solid #bfdbfe',
                          whiteSpace: 'nowrap',
                        }}>
                          {roleLabel(row.role)}
                        </span>
                      </td>
                      <td style={{ ...td, color: '#475569' }}>{row.tripTitle}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{row.destination}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: '#64748b' }}>{row.startDate || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
                        {`A$${row.estCost.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Disclaimer */}
          <p style={{ fontSize: 11, color: '#78716c', fontStyle: 'italic', marginTop: 12, lineHeight: 1.6 }}>
            Support worker cost estimates are calculated by dividing the total trip cost proportionally across all passengers.
            For exact cost tracking, sector-level cost splitting would be required.
          </p>
        </>
      )}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const card         = { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 20px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const lbl          = { display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const inp          = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, color: '#1e293b', outline: 'none', background: '#fff' };
const pill         = { padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f1f5f9', color: '#475569', fontSize: 12, fontWeight: 500, cursor: 'pointer' };
const summaryCard  = { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' };
const summaryLabel = { fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const summaryNumber= { fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 };
const sectionHeading = { fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 };
const th           = { padding: '10px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' };
const td           = { padding: '10px 12px', color: '#374151' };
const exportBtn    = { padding: '7px 16px', background: '#0d9488', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' };
